/**
 * PlanHealthSignal — calm, supportive plan health indicator
 * Placed at the top of Dashboard and The Plan.
 * States: on_track, needs_adjustment, not_set_up, error
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../App";
import { usePrivacy } from "../privacy/PrivacyProvider";
import { CheckCircle, AlertCircle, Compass, CloudOff, ArrowRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function planCopy(state, privacyStyle, journalHint) {
  const shared = privacyStyle === "fully_shared";
  const sharedPersonal = privacyStyle === "shared_personal";
  const separate = privacyStyle === "separate_shared_goals";
  const solo = privacyStyle === "solo";

  const prefix = solo ? "Your plan" : shared ? "Your shared plan" : sharedPersonal ? "Your shared plan" : separate ? "Your shared goals" : "Your plan";

  if (state === "on_track") {
    const body = journalHint
      ? journalHint
      : "What's coming in and what's going out are holding well this cycle.";
    return { title: `${prefix} is on track`, body, icon: CheckCircle, accent: "emerald" };
  }

  if (state === "needs_adjustment") {
    return {
      title: `${prefix} needs a small adjustment`,
      body: "A few things are a little off. A small change now will make the rest of the cycle easier.",
      icon: AlertCircle,
      accent: "amber",
    };
  }

  if (state === "not_set_up") {
    return {
      title: "Let's build your plan",
      body: "Once your plan is in place, everything becomes clearer and easier to manage.",
      icon: Compass,
      accent: "slate",
    };
  }

  // error
  return {
    title: "We couldn't work this out right now",
    body: "This looks like a connection issue. Your data is safe — try again in a moment.",
    icon: CloudOff,
    accent: "slate",
  };
}

const ACCENT = {
  emerald: "bg-[#1a4d3e]/5 border-[#1a4d3e]/15",
  amber: "bg-amber-50 dark:bg-amber-950/20 border-amber-200/50 dark:border-amber-900/30",
  slate: "bg-muted/30 border-border",
};
const ICON_ACCENT = {
  emerald: "bg-[#1a4d3e]/10 text-[#1a4d3e]",
  amber: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
  slate: "bg-muted text-muted-foreground",
};

export function PlanHealthSignal() {
  const { household } = useAuth();
  const { privacyStyle } = usePrivacy();
  const [health, setHealth] = useState(null);
  const [journalHint, setJournalHint] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!household?.household_id) return;
    let cancelled = false;

    const fetchHealth = async () => {
      try {
        const [healthRes, journalRes] = await Promise.all([
          fetch(`${API_URL}/api/households/${household.household_id}/plan-health`, { credentials: "include" }),
          fetch(`${API_URL}/api/households/${household.household_id}/journal/latest`, { credentials: "include" }),
        ]);
        if (cancelled) return;
        if (healthRes.ok) setHealth(await healthRes.json());
        if (journalRes.ok) {
          const jd = await journalRes.json();
          if (jd.entry?.type === "annual_intention" && jd.entry?.content) {
            const tone = jd.entry.content.toLowerCase();
            if (tone.includes("calm") || tone.includes("peace") || tone.includes("safe") || tone.includes("clear")) {
              setJournalHint(`You wanted this cycle to feel ${tone.includes("calm") ? "calmer" : tone.includes("peace") ? "peaceful" : tone.includes("safe") ? "safe" : "clearer"} — your plan is starting to support that.`);
            }
          }
        }
      } catch {
        if (!cancelled) setHealth({ state: "error" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHealth();
    return () => { cancelled = true; };
  }, [household?.household_id]);

  if (loading || !health) return null;

  const copy = planCopy(health.state, privacyStyle, health.state === "on_track" ? journalHint : null);
  const Icon = copy.icon;

  return (
    <div
      className={`rounded-xl border p-4 mb-6 ${ACCENT[copy.accent]}`}
      data-testid="plan-health-signal"
      data-health-state={health.state}
    >
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${ICON_ACCENT[copy.accent]}`}>
          <Icon className="w-4.5 h-4.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground" data-testid="plan-health-title">
            {copy.title}
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed mt-0.5" data-testid="plan-health-body">
            {copy.body}
          </p>
          {health.state === "not_set_up" && (
            <Link
              to="/plan"
              className="inline-flex items-center gap-1 text-xs font-medium text-[#1a4d3e] hover:text-[#1a4d3e]/80 mt-2 transition-colors"
              data-testid="plan-health-cta"
            >
              Get started <ArrowRight className="w-3 h-3" />
            </Link>
          )}
          {health.state === "needs_adjustment" && (
            <Link
              to="/plan?tab=expenses"
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-500 mt-2 transition-colors"
              data-testid="plan-health-cta"
            >
              Review your plan <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
